import { HttpClient, HttpParams } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { firstValueFrom, forkJoin } from 'rxjs';
import { map } from 'rxjs/operators';
import { CookieService } from 'ngx-shared-services';
import {
  ProductSitePerformanceApiItem,
  ProductSitePerformanceApiResponse,
  ProductPerformanceApiResponse,
  ProductPerformanceEventApiItem,
  SitePerformanceApiResponse,
  SitePerformanceEventApiItem,
} from '../../models/performance-api.model';
import { PerformanceInterval, SiteIntervalApiItem, SiteIntervalApiResponse } from '../../models/performance-chart.model';
import { ApiEndpointsService } from './api-endpoints.service';
import { PerformanceEvent } from '../../models/performance-event.model';
import { RegistrationTableRow } from '../../models/performance-view.model';
import { environment } from '../../environments/environment';
import { getLocalizedValue } from '../shared/utils/performance.utils';

@Injectable({ providedIn: 'root' })
export class PerformanceDetailsService {
  private readonly endPointService = inject(ApiEndpointsService);
  private readonly http = inject(HttpClient);
  private readonly cookieService = inject(CookieService);

  private get locale(): string {
    return this.cookieService.getI18NLocale() || environment.defaults.locale;
  }

  /**
   * Fetches site performances for selected site and date range
   */
  private getSitePerformances(siteId: string, startDttm: string, endDttm: string) {
    const url = `${this.endPointService.baseUrl}/sites/${siteId}/performances`;
    const params = this.buildParams(startDttm, endDttm);

    return this.http
      .get<SitePerformanceApiResponse>(url, { params, withCredentials: true })
      .pipe(map(res => res.data.map(item => this.mapToSitePerformanceEvent(item, siteId))));
  }

  /**
   * Fetches multiple performance events for selected sites and dates in parallel
   */
  getSitePerformancesByIds(siteIds: string[], startDttm: string, endDttm: string): Promise<PerformanceEvent[]> {
    if (!siteIds?.length) {
      return Promise.resolve([]);
    }

    const requests = siteIds.map(id => this.getSitePerformances(id, startDttm, endDttm));

    return firstValueFrom(forkJoin(requests).pipe(map(results => results.flat())));
  }

  /**
   * Fetches the interval data for a specific performance event, which is used to build the performance chart
   */
  getSiteIntervalData(siteId: string, eventId: string): Promise<PerformanceInterval[]> {
    const url = `${this.endPointService.baseUrl}/sites/${siteId}/events/${eventId}/intervals`;
    return firstValueFrom(
      this.http.get<SiteIntervalApiResponse>(url, { withCredentials: true }).pipe(map(res => res.data.map(item => this.toPerformanceInterval(item))))
    );
  }

  getProductIntervalData(productId: string, eventId: string): Promise<PerformanceInterval[]> {
    const url = `${this.endPointService.baseUrl}/admin/products/${encodeURIComponent(productId)}/events/${encodeURIComponent(eventId)}/intervals`;
    return firstValueFrom(
      this.http.get<SiteIntervalApiResponse>(url, { withCredentials: true }).pipe(map(res => res.data.map(item => this.toPerformanceInterval(item))))
    );
  }

  getProductSitePerformances(productId: string, eventId: string): Promise<RegistrationTableRow[]> {
    const url = `${this.endPointService.baseUrl}/admin/products/${encodeURIComponent(productId)}/events/${encodeURIComponent(eventId)}/site-performances`;
    return firstValueFrom(
      this.http
        .get<ProductSitePerformanceApiResponse>(url, { withCredentials: true })
        .pipe(map(res => res.data.map(item => this.mapToProductSitePerformance(item))))
    );
  }

  /**
   * Fetches performances for a single product and date range
   */
  private getProductPerformances(productId: string, startDttm: string, endDttm: string) {
    const url = `${this.endPointService.baseUrl}/admin/products/${encodeURIComponent(productId)}/performances`;
    const params = this.buildParams(startDttm, endDttm);

    return this.http
      .get<ProductPerformanceApiResponse>(url, { params, withCredentials: true })
      .pipe(map(res => res.data.map(item => this.mapToProductPerformanceEvent(item, productId))));
  }

  /**
   * Fetches multiple performance events for selected products and dates in parallel
   */
  getProductPerformancesByIds(productIds: string[], startDttm: string, endDttm: string): Promise<PerformanceEvent[]> {
    if (!productIds?.length) {
      return Promise.resolve([]);
    }

    const requests = productIds.map(id => this.getProductPerformances(id, startDttm, endDttm));

    return firstValueFrom(forkJoin(requests).pipe(map(results => results.flat())));
  }

  private mapToSitePerformanceEvent(item: SitePerformanceEventApiItem, siteId: string): PerformanceEvent {
    return {
      siteId: item.site_id ?? siteId,
      siteName: getLocalizedValue(item.site_name, this.locale),
      eventId: item.event_id,
      eventName: item.event_name,
      programId: item.program_id,
      programName: getLocalizedValue(item.program_name, this.locale),
      eventStartDttm: item.event_start_dttm,
      eventEndDttm: item.event_end_dttm,
      eventType: item.event_type,
      timezone: item.timezone,
      dispatchedDttm: item.dispatched_dttm,
      createdDttm: item.created_dttm,
      updatedDttm: item.updated_dttm,
      updatedBy: item.updated_by,
      perfGoodDq: item.perf_good_dq,
      perfFinal: item.perf_final,
      summaryOnly: item.summary_only,
      displayPreliminaryData: true,
      enrollmentValue: item.perf_enrollment_kw,
      performancePercentage: item.performance_percentage,
      performanceValue: item.perf_summary_kw,
      performanceGap: item.performance_gap,
      organizationId: item.org_id ?? '',
      organizationName: getLocalizedValue(item.org_display_labels, this.locale),
      productId: '',
      productName: getLocalizedValue(item.product_name, this.locale),
    };
  }

  private buildParams(startDttm?: string, endDttm?: string): HttpParams {
    let params = new HttpParams();
    if (startDttm) params = params.set('start_dttm', startDttm);
    if (endDttm) params = params.set('end_dttm', endDttm);
    return params;
  }

  private mapToProductPerformanceEvent(item: ProductPerformanceEventApiItem, productId: string): PerformanceEvent {
    return {
      productId,
      eventId: item.event_id,
      eventName: item.event_name,
      programId: item.program_id,
      programName: getLocalizedValue(item.program_name, this.locale),
      siteId: '',
      siteName: '',
      eventStartDttm: item.event_start_dttm,
      eventEndDttm: item.event_end_dttm,
      eventType: item.event_type,
      timezone: item.timezone,
      dispatchedDttm: item.dispatched_dttm,
      createdDttm: item.created_dttm,
      updatedDttm: item.updated_dttm,
      updatedBy: item.updated_by,
      enrollmentValue: item.perf_enrollment_kw,
      performancePercentage: item.performance_percentage,
      performanceValue: item.perf_summary_kw,
      performanceGap: item.performance_gap,
      organizationId: '',
      organizationName: '',
      productName: getLocalizedValue(item.product_name, this.locale),
    };
  }

  private toPerformanceInterval(item: SiteIntervalApiItem): PerformanceInterval {
    return {
      interval_dttm: item.interval_dttm,
      event_id: item.event_id,
      demand_kw: item.demand_kw,
      baseline_kw: item.baseline_kw,
      dispatch_target_kw: item.dispatch_target_kw,
      nomination_kw: item.nomination_kw,
    };
  }

  private mapToProductSitePerformance(item: ProductSitePerformanceApiItem): RegistrationTableRow {
    return {
      id: item.site_id,
      site: getLocalizedValue(item.site_name, this.locale),
      organization: getLocalizedValue(item.org_display_labels, this.locale),
      organizationId: item.org_id ?? '',
      registrationLabel: '',
      eventDate: item.event_start_dttm,
      enrolled: item.perf_enrollment_kw,
      delivered: item.perf_summary_kw,
      performance: item.performance_percentage,
      dataQuality: item.perf_final ? 'Final' : 'Preliminary',
    };
  }
}
